import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Normalizes a color name for reliable comparisons.
   *
   * @param {unknown} value
   * @returns {string}
   */
  #normalize(value) {
    return String(value || '')
      .trim()
      .toLocaleLowerCase()
      .replace(/\s+/g, ' ');
  }

  /**
   * Checks whether an option name represents a color option.
   *
   * @param {unknown} value
   * @returns {boolean}
   */
  #isColorOption(value) {
    const normalizedValue = this.#normalize(value);

    return (
      normalizedValue.includes('farbe') ||
      normalizedValue.includes('color') ||
      normalizedValue.includes('colour')
    );
  }

  /**
   * Keeps the existing XSCRATCH gallery in place for mapped color changes.
   * The gallery synchronizer has already selected the correct 3D slide before
   * Shopify finishes loading the remaining variant information. Replacing the
   * gallery here would briefly restore the title image and cause the visible
   * slideshow sweep reported on the product page.
   *
   * @param {ProductSelectEvent} event
   * @returns {boolean}
   */
  #shouldPreserveMappedGallery(event) {
    if (!(event.target instanceof Element)) return false;

    const picker = event.target.closest('variant-picker');
    const optionValueId = event.detail?.optionValueId;
    const mapElement = this.querySelector('[data-xscratch-3d-media-map]');

    if (!picker || !optionValueId || !mapElement) return false;

    const changedOption = Array.from(picker.querySelectorAll('[data-option-value-id]')).find(
      (option) => option.getAttribute('data-option-value-id') === String(optionValueId)
    );

    if (
      !(changedOption instanceof HTMLInputElement) &&
      !(changedOption instanceof HTMLOptionElement)
    ) {
      return false;
    }

    if (!this.#isColorOption(changedOption.dataset.optionName)) return false;

    const selectedColor = changedOption.value;
    const productScope =
      this.closest('product-information, featured-product-information, quick-add-dialog') ||
      this.closest('.shopify-section');

    if (
      this.#normalize(productScope?.dataset.xscratchSelected3dValue) !==
      this.#normalize(selectedColor)
    ) {
      return false;
    }

    try {
      const mediaMap = JSON.parse(mapElement.textContent || '{}');

      return Object.keys(mediaMap).some(
        (color) => this.#normalize(color) === this.#normalize(selectedColor)
      );
    } catch {
      return false;
    }
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    if (this.#shouldPreserveMappedGallery(event)) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}